import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Card, Field, Input, Spinner, Toggle, applyTheme } from "@aigntx/ui";
import { endpoints } from "../api";
import { useAuth } from "../auth";
import { ThemeControls } from "../ThemeControls";
import { BrandMark } from "../BrandMark";
import { TestSignIn } from "../components/TestSignIn";
import type { TenantPublic } from "../types";

/** Shared shell so the front door and its sign-in page are visibly one place. */
function CompanyFrame({
  company,
  children,
}: {
  company: TenantPublic;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="landing">
      <header className="landing__top">
        <Link className="landing__brandlink" to={`/t/${company.slug}`}>
          {company.theme.logo ? (
            <img src={company.theme.logo} alt="" className="landing__logo" />
          ) : (
            <BrandMark icon={company.theme.icon} size={34} />
          )}
          <span className="brand__name">{company.name}</span>
        </Link>
        <ThemeControls />
      </header>
      {children}
      <footer className="landing__bottom muted">
        <span>
          {company.name} · <span className="landing__powered">AiGENTC, powered by NXGN AI</span>
        </span>
        <span>
          <Link to="/privacy">{t("legal.privacy")}</Link> ·{" "}
          <Link to="/terms">{t("legal.terms")}</Link>
        </span>
      </footer>
    </div>
  );
}

function useCompany(slug: string) {
  const [company, setCompany] = useState<TenantPublic | null>(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    endpoints
      .publicTenant(slug)
      .then((c) => {
        setCompany(c);
        applyTheme(c.theme); // wear the company's identity — this is their page
      })
      .catch(() => setNotFound(true));
  }, [slug]);
  return { company, notFound };
}

function NotFound({ slug }: { slug: string }) {
  const { t } = useTranslation();
  return (
    <div className="auth">
      <Card className="auth__card">
        <h1 className="auth__title">{t("landing.notFound")}</h1>
        <p className="auth__sub">{t("landing.notFoundHint", { slug })}</p>
        <Link className="auth__link" to="/login">
          {t("landing.platformSignIn")}
        </Link>
      </Card>
    </div>
  );
}

/**
 * A company's front door at /t/<slug> — its logo, its name, and one way in.
 *
 * The platform sign-in page is for the platform's own staff. Everyone who works
 * with a consultancy — its owners, its engineers, the contractors it created
 * accounts for — starts here instead.
 */
export function TenantLandingPage() {
  const { slug = "" } = useParams();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { company, notFound } = useCompany(slug);

  if (user) return <Navigate to="/" replace />;
  if (notFound) return <NotFound slug={slug} />;
  if (!company)
    return (
      <div className="auth">
        <Spinner />
      </div>
    );

  return (
    <CompanyFrame company={company}>
      <main className="landing__hero">
        <div className="landing__mark">
          {company.theme.logo ? (
            <img src={company.theme.logo} alt="" className="landing__logo landing__logo--big" />
          ) : (
            <BrandMark icon={company.theme.icon} size={76} />
          )}
        </div>
        <h1 className="landing__title">{company.name}</h1>
        <p className="landing__powered landing__powered--hero">AiGENTC, powered by NXGN AI</p>
        <p className="landing__lede">
          {company.about || t("landing.lede")}
        </p>
        <Link className="ax-btn ax-btn--primary landing__cta" to={`/t/${slug}/login`}>
          {t("landing.signIn")}
        </Link>
        <p className="landing__note muted">{t("landing.afterSignIn")}</p>
      </main>
    </CompanyFrame>
  );
}

/** The company's own sign-in page — same branding, no platform chrome. */
export function TenantLoginPage() {
  const { slug = "" } = useParams();
  const { t } = useTranslation();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const { company, notFound } = useCompany(slug);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/" replace />;
  if (notFound) return <NotFound slug={slug} />;
  if (!company)
    return (
      <div className="auth">
        <Spinner />
      </div>
    );

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password, remember);
      navigate("/"); // the hub decides what this person actually sees
    } catch {
      setError(t("login.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <CompanyFrame company={company}>
      <main className="landing__signin">
        <Card className="landing__card">
          <h1 className="auth__title">{t("landing.signIn")}</h1>
          <p className="auth__sub">{t("landing.signInHint", { name: company.name })}</p>
          <form className="auth__form" onSubmit={submit}>
            <Field label={t("login.email")} htmlFor="lemail">
              <Input
                id="lemail"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Field>
            <Field label={t("login.password")} htmlFor="lpassword" error={error || undefined}>
              <Input
                id="lpassword"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Field>
            <div className="auth__row">
              <Toggle checked={remember} onChange={setRemember} label={t("login.remember")} />
              <Link className="auth__link" to="/forgot">
                {t("login.forgot")}
              </Link>
            </div>
            <Button type="submit" loading={busy}>
              {busy ? t("login.signingIn") : t("login.submit")}
            </Button>
          </form>
          <TestSignIn
            slug={slug}
            onPick={(e, p) => {
              setEmail(e);
              setPassword(p);
              setError("");
            }}
          />
          <p className="landing__foot muted">
            {t("landing.noAccount")}{" "}
            <Link className="auth__link" to="/login">
              {t("landing.platformSignIn")}
            </Link>
          </p>
        </Card>
      </main>
    </CompanyFrame>
  );
}
